/**
 * The curated icon set a user can pin to a block or an example.
 *
 * Kept small on purpose: a picker with every lucide icon is a search problem,
 * twenty grouped ones is a glance. The stored value is the `id` string — the
 * `icon` column zenitailab !854 adds to assistant_template_section.
 */
import {
  AlertTriangle,
  BookOpen,
  Braces,
  Building2,
  Calendar,
  CircleHelp,
  CreditCard,
  FileText,
  Gift,
  Handshake,
  Heart,
  ListChecks,
  MapPin,
  MessageCircle,
  Package,
  Scale,
  ShieldAlert,
  ShoppingCart,
  Sparkles,
  Target,
  Truck,
  User,
  Users,
  Wallet,
} from "lucide-react";

/** Groups become the tabs of the picker. */
export const ICON_GROUPS = [
  {
    id: "identity",
    label: "Identity",
    icons: [
      { id: "user", Icon: User, label: "Person" },
      { id: "sparkles", Icon: Sparkles, label: "Personality" },
      { id: "target", Icon: Target, label: "Scope" },
      { id: "heart", Icon: Heart, label: "Tone" },
      { id: "message-circle", Icon: MessageCircle, label: "Conversation" },
      { id: "building", Icon: Building2, label: "Company" },
    ],
  },
  {
    id: "commerce",
    label: "Commerce",
    icons: [
      { id: "wallet", Icon: Wallet, label: "Payments" },
      { id: "credit-card", Icon: CreditCard, label: "Card" },
      { id: "shopping-cart", Icon: ShoppingCart, label: "Orders" },
      { id: "package", Icon: Package, label: "Products" },
      { id: "truck", Icon: Truck, label: "Shipping" },
      { id: "gift", Icon: Gift, label: "Promotions" },
    ],
  },
  {
    id: "support",
    label: "Support",
    icons: [
      { id: "help", Icon: CircleHelp, label: "FAQ" },
      { id: "users", Icon: Users, label: "Handoff" },
      { id: "handshake", Icon: Handshake, label: "Escalation" },
      { id: "alert-triangle", Icon: AlertTriangle, label: "Warning" },
      { id: "shield-alert", Icon: ShieldAlert, label: "Boundaries" },
      { id: "calendar", Icon: Calendar, label: "Scheduling" },
    ],
  },
  {
    id: "content",
    label: "Content",
    icons: [
      { id: "file-text", Icon: FileText, label: "Instructions" },
      { id: "book-open", Icon: BookOpen, label: "Knowledge" },
      { id: "list-checks", Icon: ListChecks, label: "Rules" },
      { id: "braces", Icon: Braces, label: "Output format" },
      { id: "scale", Icon: Scale, label: "Policy" },
      { id: "map-pin", Icon: MapPin, label: "Location" },
    ],
  },
];

const BY_ID = Object.fromEntries(
  ICON_GROUPS.flatMap((g) => g.icons).map((i) => [i.id, i])
);

/**
 * Name keywords → icon, used ONLY to suggest one inside the picker.
 *
 * Never to render with. A suggested icon and a chosen one look identical on
 * screen, so displaying a guess means the app quietly decides for the user and
 * gives them no way to tell which icons are theirs.
 *
 * It is also a biased list — written by reading the avatars that happened to
 * exist — so it fits a support/commerce bot and says little about anything
 * else. That is tolerable for a suggestion you can ignore, and was not
 * tolerable as the thing being drawn.
 */
const KEYWORD_GUESSES = [
  [/identidad|identity|rol\b|role|qui[eé]n/i, "user"],
  [/tono|tone|estilo|style|voz|voice/i, "heart"],
  [/alcance|scope|l[ií]mite|boundar|no hac|restric/i, "target"],
  [/handoff|escal|humano|human|agente|asesor/i, "users"],
  [/pago|payment|precio|price|cobr|wallet/i, "wallet"],
  [/env[ií]o|shipping|entrega|deliver/i, "truck"],
  [/producto|product|cat[aá]logo|catalog|art[ií]culo/i, "package"],
  [/pedido|orden|order|compra|carrito/i, "shopping-cart"],
  [/faq|pregunt|question|duda/i, "help"],
  [/formato|format|salida|output|json|estructura/i, "braces"],
  [/instruc|instruction|gu[ií]a|guide|prop[oó]sito/i, "file-text"],
  [/conocimiento|knowledge|info|datos|data/i, "book-open"],
  [/regla|rule|pol[ií]tica|policy|norma/i, "list-checks"],
  [/reserva|cita|agenda|schedul|fecha|calendar|taller/i, "calendar"],
  [/promo|descuento|discount|oferta|regalo/i, "gift"],
  [/ubicaci|direcci|location|address|sucursal|tienda/i, "map-pin"],
];

/**
 * The icon to render for a block: the one the user picked, or a neutral glyph.
 *
 * No guessing here — see KEYWORD_GUESSES.
 */
export const resolveBlockIcon = (section, { isExample = false } = {}) => {
  if (section?.icon && BY_ID[section.icon]) return BY_ID[section.icon].Icon;
  return isExample ? MessageCircle : FileText;
};

/** True when the icon on screen is just the neutral placeholder. */
export const hasIcon = (section) => Boolean(section?.icon && BY_ID[section.icon]);

/**
 * What the picker offers for a block with no icon yet. Returns the icon entry
 * ({ id, Icon, label }) or null — the user still has to accept it.
 */
export const suggestIconForName = (name) => {
  const guess = KEYWORD_GUESSES.find(([re]) => re.test(name || ""));
  return guess ? BY_ID[guess[1]] : null;
};

/** Look an icon up by its stored id. */
export const iconById = (id) => BY_ID[id]?.Icon || null;

/* ------------------------------------------------------------------ */
/*  Block kind                                                         */
/*                                                                     */
/*  A reading aid, not a stored field: the backend has no block type.  */
/*  It follows the icon the user picked. No icon, no kind — guessing   */
/*  one from the name would be the same lie the icon used to tell.     */
/* ------------------------------------------------------------------ */

const KIND_STYLES = {
  Role: "border-purple-500/30 bg-purple-600/15 text-purple-200",
  Style: "border-green-500/30 bg-green-600/15 text-green-200",
  Rules: "border-blue-500/30 bg-blue-600/15 text-blue-200",
  Escalation: "border-orange-500/30 bg-orange-600/15 text-orange-200",
  Output: "border-pink-500/30 bg-pink-600/15 text-pink-200",
  Knowledge: "border-amber-500/30 bg-amber-600/15 text-amber-200",
  Context: "border-teal-500/30 bg-teal-600/15 text-teal-200",
};

/** Which kind each icon in the set implies. */
const KIND_BY_ICON = {
  user: "Role",
  building: "Role",
  sparkles: "Style",
  heart: "Style",
  "message-circle": "Style",
  target: "Rules",
  "list-checks": "Rules",
  scale: "Rules",
  "shield-alert": "Rules",
  users: "Escalation",
  handshake: "Escalation",
  "alert-triangle": "Escalation",
  braces: "Output",
  "book-open": "Knowledge",
  help: "Knowledge",
  wallet: "Knowledge",
  "credit-card": "Knowledge",
  "shopping-cart": "Knowledge",
  package: "Knowledge",
  truck: "Knowledge",
  gift: "Knowledge",
  calendar: "Knowledge",
  "map-pin": "Knowledge",
  "file-text": "Context",
};

/** The kind implied by the chosen icon, or null when none was chosen. */
export const resolveBlockKind = (section) => {
  if (!hasIcon(section)) return null;

  const label = KIND_BY_ICON[section.icon] || "Context";
  return { label, className: KIND_STYLES[label] };
};

/** Neutral chrome for a block with no icon — used where a kind's colour would go. */
export const NEUTRAL_KIND_CLASS = "border-white/10 bg-white/5 text-gray-400";
